import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  setGoogleTranslateLanguage,
  getCurrentLanguageFromCookie,
} from '../utils/translate';

const LanguageContext = createContext(null);

export const LANGUAGE_OPTIONS = [
  { code: 'fr', label: 'Français', flag: '/France.svg' },
  { code: 'en', label: 'English', flag: '/usa.svg' },
  { code: 'es', label: 'Español', flag: '/spain.svg' },
  { code: 'it', label: 'Italiano', flag: '/italy.svg' },
];

export function LanguageProvider({ children }) {
  // Read the current language from the cookie/localStorage on mount.
  // setGoogleTranslateLanguage triggers window.location.reload(), so a full
  // re-mount always happens after switching — but we still use useState so the
  // selector UI is always accurate on the initial render without a flash.
  const [currentLanguage, setCurrentLanguage] = useState(() =>
    getCurrentLanguageFromCookie()
  );

  // Keep in sync if another tab changes the language via localStorage
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === 'user-language') {
        setCurrentLanguage(e.newValue || 'fr');
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  // Wrap setGoogleTranslateLanguage so the local state updates immediately
  // before the reload, giving instant UI feedback (flag/label swap).
  const setLanguage = useCallback((lang) => {
    setCurrentLanguage(lang);
    setGoogleTranslateLanguage(lang);
  }, []);

  return (
    <LanguageContext.Provider
      value={{
        currentLanguage,
        setLanguage,
        options: LANGUAGE_OPTIONS,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return ctx;
}
